import { Cesium3DTileFeature, defined } from "@cesium/engine";
import {
  DitapAlert,
  EditUtils,
  InteractionRemove,
  InteractionSelect,
  MeasurementVolume,
  Utils,
} from "../../..";
import DitapPopup from "./DitapPopup";

/*
 * 선택한 객체가 여러개일때 ID를 표시하는 팝업 클래스.<br>
 * A Pop-up that displays the IDs of multiple selected objects.
 * @extends DitapPopup
 *
 * @alias PopupMultiSelect
 * @constructor
 *
 * @param {InteractionSelect|InteractionRemove} interaction 팝업을 호출한 Interaction의 인스턴스.</br>
 * The instance of Interaction that invoked the PopupMultiSelect
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 */
class PopupMultiSelect extends DitapPopup {
  constructor(interaction, viewer) {
    // 부모
    const parent = viewer.container;
    super(parent, viewer.ditapPaneManager);

    this.viewer = viewer;

    let text;
    if (interaction instanceof InteractionSelect) {
      text = "선택";
    } else if (interaction instanceof InteractionRemove) {
      text = "삭제";
    }
    // 다중선택박스 생성
    const propertyBox = this.createMultiSelectBox(text);

    // 헤더 추가
    this.prependHeader(propertyBox["header"]["title"]);

    // 컨텐츠 추가
    this.appendSubTitle(propertyBox["content"]["subTitle"]);
    this.appendContent(propertyBox["content"]["selectList"]);

    // 노드 세팅
    this.selectUl = propertyBox["content"]["selectList"];

    this.interaction = interaction;

    this.changeCloseBtn();
    const isMobile = Utils.isBodyWidthLessThan600();
    if (!isMobile) {
      this.enableDrag();
      this.enableTouch();
    }

    // 푸터 삭제
    this.footer.remove();

    // 스크롤 추가
    this.wrapper.classList.add("ditap-scroll-popup");
  }

  /**
   * 아이디 목록 삭제.</br>
   * Remove the list of id property.
   */
  removePropertyList() {
    // 팝업에 표시된 기존 개체 ID 목록을 삭제합니다.
    while (this.selectUl.firstChild) {
      this.selectUl.removeChild(this.selectUl.lastChild);
    }
  }

  /**
   * 다중 선택 팝업 요소 생성.</br>
   * Create PopupMultiSelect Element.
   * @param {string} text 인터렉션 이름.</br>The name of interaction.
   * @returns {object} 팝업 요소 객체.</br>PopupMultiSelect Element.
   */
  createMultiSelectBox(text) {
    // * 다중선택박스 생성
    // WRAPPER
    this.wrapper.classList.add(
      "ditap-scroll-popup",
      "ditap-popup-mobile",
      "ditap-mobile-bottom",
      "header-top",
      "draggable",
      "ditap-multiselect-popup"
    );
    this.wrapper.classList.remove("ditap-size-popup");

    // # 헤더
    const title = document.createElement("h2");
    title.classList.add("ditap-popup-title");
    title.innerText = text;

    // # 컨텐츠
    const subTitle = document.createElement("p");
    subTitle.classList.add("ditap-popup-subtitle");
    subTitle.innerText = "객체 리스트를 선택하세요.";
    const selectList = document.createElement("ul");
    selectList.classList.add(
      "ditap-select-list",
      "js-ditap-multiple-select-list"
    );

    return {
      header: {
        title: title,
      },
      content: {
        subTitle: subTitle,
        selectList: selectList,
      },
    };
  }

  getSelectedColor(interaction, pickedObject) {
    if (EditUtils.isCesium3DTileset(pickedObject)) {
      const { red, green, blue, alpha } = interaction.selectedColor;
      return `color("rgba(${red * 255}, ${green * 255}, ${
        blue * 255
      }, ${alpha})")`;
    }
    EditUtils.setOriginalColor(pickedObject);
    return interaction.selectedColor;
  }

  /**
   * 아이디 목록에 아이디 추가.<br>
   * Adds a list of object ids to the PopupMultiSelect.
   * @param {object} objects 선택된 객체.</br>The selected objects.
   * @param {DitapAlert} alert 알림 인스턴스.</br>The DitapAlert.
   */
  addMultiList(objects, alert) {
    // * 다중선택팝업 - 팝업리스트 추가
    // 기존 리스트 삭제
    this.removePropertyList();
    const interaction = this.interaction;

    if (objects.length > 0) {
      objects.forEach((pickedObject) => {
        const id = EditUtils.getSelectObjectId(this.viewer, pickedObject);
        const li = document.createElement("li");
        const button = document.createElement("button");

        li.className = "ditap-select-item";
        button.className = "ditap-select-item-btn";

        button.textContent = id;

        li.appendChild(button);
        this.selectUl.appendChild(li);

        this.highlightObject = undefined;
        // 목록 hover 이벤트 생성
        li.addEventListener("mouseover", () => {
          if (defined(pickedObject)) {
            this.interaction.highlightedObjects = EditUtils.setHighlightObjects(
              this.interaction.highlightedObjects,
              this.interaction.hoverColor,
              this.interaction.selectedObject,
              [pickedObject]
            );
          }
        });

        // 목록 click이벤트 생성
        li.addEventListener("click", () => {
          this.close();
          if (
            this.viewer.ditapStorage.getDitapEntity(pickedObject) instanceof
              MeasurementVolume &&
            this.viewer.ditapStorage.getDitapEntity(pickedObject).positions
              .length === 0
          ) {
            // 경고창 생성
            const warningAelrt = new DitapAlert({
              type: "warning",
              title: "경고",
              content1: "체적 계산 중인 경우 선택이 불가합니다.",
              buttons: "ONLYOK",
              open: false,
              callback: (alert) => {
                alert.destroy();
              },
              closeCallback: (alert) => {
                alert.destroy();
              },
            });
            warningAelrt.open();
            return;
          } else if (interaction instanceof InteractionSelect) {
            // 선택한 객체 저장
            interaction.selectedObject = pickedObject;

            // 선택 객체 색상 변경
            interaction.highlightedObjects = interaction.highlightedObjects.filter(
              (obj) => obj !== pickedObject
            );
            const selectedColor = this.getSelectedColor(
              interaction,
              pickedObject
            );
            EditUtils.changeColor(pickedObject, selectedColor);

            if (
              pickedObject instanceof Cesium3DTileFeature ||
              EditUtils.isCesium3DTileset(pickedObject)
            ) {
              interaction.openProperty();
              return;
            }

            // 더보기 버튼 생성
            interaction.openMoreButton(pickedObject);

            // 선택한 객체로 카메라 줌
            EditUtils.zoomToObject(this.interaction.viewer, pickedObject);
          } else if (interaction instanceof InteractionRemove) {
            const selectedColor = this.getSelectedColor(
              interaction,
              pickedObject
            );
            EditUtils.changeColor(pickedObject, selectedColor);

            interaction.editRemove.removeModelByType(pickedObject, alert);
          }
        });
      });
    }
  }
}
export default PopupMultiSelect;
